import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Every *_datetime field from the backend is a naive-UTC string with no
// offset suffix -- plain `new Date(x)` on that parses it as browser-local
// instead, silently shifting every pickup/delivery/time-off moment by the
// browser's UTC offset (wrong day, wrong lane on the calendar, wrong
// departure-bar position, all without erroring). Same fix DispatcherBoard.jsx
// already uses for the same shape of timestamp.
function parseUtcNaive(isoLike) {
  if (!isoLike) return null;
  const iso = isoLike.endsWith("Z") || /[+-]\d\d:\d\d$/.test(isoLike) ? isoLike : `${isoLike}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Outlook-style week grid: one row per driver, one column per day, and loads
// / time off render as bars that actually SPAN the days they cover (not a
// repeated chip in every day cell they touch). Time off entered here is the
// real thing match_truck_for_load now checks -- a driver on time off won't
// be treated as available until it ends, but still surfaces as a candidate
// once they're back (see driver_time_off.py).
//
// A load's bar spans from an ESTIMATED departure time (pickup time, pulled
// back by a straight-line-distance deadhead estimate from the truck's last
// known position) through the delivery time -- not just the two point
// labels this used to show. That deadhead estimate is a rough haversine/50mph
// guess for sizing the bar, not a routed prediction; the Route tab's saved
// trip plan is the real routed ETA.
const ESTIMATED_DEADHEAD_MPH = 50;
const LANE_HEIGHT = 30;
const ROW_PADDING = 12;
const MIN_ROW_HEIGHT = 52;

export default function DriverCalendar() {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loads, setLoads] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [addingFor, setAddingFor] = useState(null); // { driverId, date }
  const [editingTimeOff, setEditingTimeOff] = useState(null); // the raw time-off record being edited
  const [savingTimeOff, setSavingTimeOff] = useState(false);
  const [reassigning, setReassigning] = useState(null); // driverId currently showing the truck picker
  const [loadDetail, setLoadDetail] = useState(null); // the load record being viewed

  function reload() {
    setLoading(true);
    Promise.all([
      axios.get(`${apiBaseUrl}/drivers/?limit=300`, authHeaders()),
      axios.get(`${apiBaseUrl}/trucks/?limit=300`, authHeaders()),
      axios.get(`${apiBaseUrl}/loads/?limit=500`, authHeaders()),
      axios.get(`${apiBaseUrl}/driver_time_off/?limit=500`, authHeaders()),
    ])
      .then(([dRes, tRes, lRes, toRes]) => {
        setDrivers(dRes.data.records || dRes.data || []);
        setTrucks(tRes.data.records || tRes.data || []);
        setLoads(lRes.data.records || lRes.data || []);
        setTimeOff(toRes.data.records || toRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(reload, []);

  const truckByDriverId = useMemo(() => {
    const map = {};
    trucks.forEach((t) => {
      const did = t.current_driver_id?.id || t.current_driver_id;
      if (did) map[did] = t;
    });
    return map;
  }, [trucks]);

  const truckById = useMemo(() => {
    const map = {};
    trucks.forEach((t) => { map[t.id] = t; });
    return map;
  }, [trucks]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekStartUtc = useMemo(
    () => new Date(Date.UTC(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate())),
    [weekStart]
  );
  const weekEndUtc = useMemo(() => new Date(weekStartUtc.getTime() + 7 * 24 * 3600 * 1000), [weekStartUtc]);

  // One event list per driver -- time off (full record, not split per day)
  // plus loads (spanning estimated-departure -> delivery), in real UTC
  // moments so a multi-day span is a single event, not a chip repeated per
  // cell.
  const eventsByDriver = useMemo(() => {
    const map = {};
    drivers.forEach((d) => { map[d.id] = []; });

    timeOff.forEach((r) => {
      const did = r.driver_id?.id || r.driver_id;
      if (!map[did]) return;
      const start = r.start_datetime ? parseUtcNaive(r.start_datetime) : null;
      const end = r.end_datetime ? parseUtcNaive(r.end_datetime) : null;
      if (!start || !end) return;
      map[did].push({ kind: "timeoff", id: r.id, start, end, note: r.note, raw: r });
    });

    loads.forEach((l) => {
      if ((l.status || "").toLowerCase() === "cancelled") return;
      const assignedId = l.assigned_truck_id?.id || l.assigned_truck_id;
      if (!assignedId) return;
      const truck = truckById[assignedId];
      if (!truck) return;
      const did = truck.current_driver_id?.id || truck.current_driver_id;
      if (!did || !map[did]) return;
      if (!l.pickup_datetime || !l.delivery_datetime) return;

      const pickup = parseUtcNaive(l.pickup_datetime);
      const delivery = parseUtcNaive(l.delivery_datetime);
      if (!pickup || !delivery) return;
      let departure = pickup;
      if (truck.current_lat != null && truck.current_lng != null && l.origin_lat != null && l.origin_lng != null) {
        const deadheadMiles = haversineMiles(
          Number(truck.current_lat), Number(truck.current_lng),
          Number(l.origin_lat), Number(l.origin_lng)
        );
        const deadheadHours = deadheadMiles / ESTIMATED_DEADHEAD_MPH;
        departure = new Date(pickup.getTime() - deadheadHours * 3600 * 1000);
      }
      map[did].push({ kind: "load", id: l.id, start: departure, end: delivery, pickup, delivery, raw: l });
    });

    return map;
  }, [drivers, timeOff, loads, truckById]);

  // Clip each driver's events to the visible week and greedily assign lanes
  // (classic interval-scheduling layout) so overlapping events stack
  // instead of colliding.
  function layoutDriverEvents(driverId) {
    const events = (eventsByDriver[driverId] || [])
      .filter((e) => e.start < weekEndUtc && e.end > weekStartUtc)
      .map((e) => {
        const clipStart = e.start < weekStartUtc ? weekStartUtc : e.start;
        const clipEnd = e.end > weekEndUtc ? weekEndUtc : e.end;
        const startCol = Math.floor((clipStart - weekStartUtc) / (24 * 3600 * 1000));
        const endCol = Math.max(startCol, Math.ceil((clipEnd - weekStartUtc) / (24 * 3600 * 1000)) - 1);
        const continuesBefore = e.start < weekStartUtc;
        const continuesAfter = e.end > weekEndUtc;
        // The bar spans whole day COLUMNS (startCol..endCol), but the real
        // event usually starts/ends partway through its first/last day --
        // without this, a time off ending at 14:00 visually covered the
        // entire last day. Inset the bar by that leftover fraction of the
        // day, expressed as a % of the bar's OWN total width (not the day
        // column's), so it works regardless of each day column's actual
        // pixel width. Skipped on whichever edge is clipped by the visible
        // week -- that edge should stay flush against the boundary (with
        // the continues-arrow), not pretend to know a fraction it can't.
        const numDays = endCol - startCol + 1;
        const dayMs = 24 * 3600 * 1000;
        const startDayBoundary = weekStartUtc.getTime() + startCol * dayMs;
        const endDayBoundaryExclusive = weekStartUtc.getTime() + (endCol + 1) * dayMs;
        const startInsetPct = continuesBefore ? 0 : ((clipStart.getTime() - startDayBoundary) / dayMs / numDays) * 100;
        const endInsetPct = continuesAfter ? 0 : ((endDayBoundaryExclusive - clipEnd.getTime()) / dayMs / numDays) * 100;
        return {
          ...e, startCol, endCol, continuesBefore, continuesAfter,
          startInsetPct, endInsetPct,
        };
      })
      .sort((a, b) => a.start - b.start);

    const laneEnds = [];
    events.forEach((e) => {
      let lane = laneEnds.findIndex((endCol) => endCol < e.startCol);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(e.endCol); }
      else laneEnds[lane] = e.endCol;
      e.lane = lane;
    });
    return { events, laneCount: Math.max(1, laneEnds.length) };
  }

  const driverLayouts = useMemo(() => {
    const map = {};
    drivers.forEach((d) => { map[d.id] = layoutDriverEvents(d.id); });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers, eventsByDriver, weekStartUtc, weekEndUtc]);

  // Both go through /fleet/save-time-off (not the generic driver_time_off
  // endpoint directly) because converting the dispatcher's typed local wall
  // clock into a real UTC instant needs a real per-timezone conversion --
  // the backend does that with the same tz_lookup machinery the broker-call
  // wizard already uses for pickup/delivery, given the explicit timezone
  // the dispatcher picked in the modal.
  function saveTimeOff(driverId, startLocal, endLocal, timezone, note) {
    setSavingTimeOff(true);
    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/save-time-off`,
        { driver_id: driverId, start_local: startLocal, end_local: endLocal, timezone, note: note || null },
        authHeaders()
      )
      .then(() => {
        setAddingFor(null);
        reload();
      })
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || "Couldn't save time off"))
      .finally(() => setSavingTimeOff(false));
  }

  function updateTimeOff(id, startLocal, endLocal, timezone, note) {
    setSavingTimeOff(true);
    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/save-time-off`,
        { id, start_local: startLocal, end_local: endLocal, timezone, note: note || null },
        authHeaders()
      )
      .then(() => {
        setEditingTimeOff(null);
        reload();
      })
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || "Couldn't update time off"))
      .finally(() => setSavingTimeOff(false));
  }

  function deleteTimeOff(id) {
    axios
      .delete(`${apiBaseUrl}/driver_time_off/${id}`, authHeaders())
      .then(reload)
      .catch((err) => window.alert(err.response?.data?.detail || "Couldn't remove time off"));
  }

  function reassignTruck(driverId, newTruckId) {
    const oldTruck = truckByDriverId[driverId];
    const requests = [];
    if (newTruckId) {
      requests.push(axios.put(`${apiBaseUrl}/trucks/${newTruckId}`, { current_driver_id: driverId }, authHeaders()));
    }
    if (oldTruck && oldTruck.id !== newTruckId) {
      requests.push(axios.put(`${apiBaseUrl}/trucks/${oldTruck.id}`, { current_driver_id: null }, authHeaders()));
    }
    Promise.all(requests)
      .then(() => {
        setReassigning(null);
        reload();
      })
      .catch((err) => window.alert(err.response?.data?.detail || "Couldn't reassign truck"));
  }

  const gridTemplateRows = `auto ${drivers
    .map((d) => `${Math.max(MIN_ROW_HEIGHT, driverLayouts[d.id]?.laneCount * LANE_HEIGHT + ROW_PADDING || MIN_ROW_HEIGHT)}px`)
    .join(" ")}`;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Driver Calendar</h1>
          <div style={styles.subtitle}>Assigned trucks, loads, and time off -- one week at a time.</div>
        </div>
        <div style={styles.weekNav}>
          <button style={styles.navBtn} onClick={() => setWeekStart((d) => addDays(d, -7))}><ChevronLeft size={16} /></button>
          <div style={styles.weekLabel}>
            <CalendarDays size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {formatShort(days[0])} - {formatShort(days[6])}
          </div>
          <button style={styles.navBtn} onClick={() => setWeekStart((d) => addDays(d, 7))}><ChevronRight size={16} /></button>
          <button style={styles.todayBtn} onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</button>
        </div>
      </div>

      <div style={styles.gridWrap}>
        {loading && <div style={styles.loadingMsg}>Loading…</div>}
        {!loading && drivers.length === 0 && <div style={styles.loadingMsg}>No drivers yet.</div>}
        {!loading && drivers.length > 0 && (
          <div style={{ ...styles.calendarGrid, gridTemplateRows }}>
            <div style={{ ...styles.th, ...styles.driverCol, gridColumn: 1, gridRow: 1 }}>Driver</div>
            {days.map((d, dayIdx) => (
              <div key={d.toISOString()} style={{ ...styles.th, gridColumn: dayIdx + 2, gridRow: 1 }}>
                {d.toLocaleDateString("en-US", { weekday: "short" })}
                <div style={styles.dayNum}>{d.getDate()}</div>
              </div>
            ))}

            {drivers.map((d, driverIdx) => {
              const truck = truckByDriverId[d.id];
              const rowNum = driverIdx + 2;
              const { events } = driverLayouts[d.id] || { events: [] };
              return (
                <React.Fragment key={d.id}>
                  <div style={{ ...styles.driverCell, gridColumn: 1, gridRow: rowNum }}>
                    <div style={styles.driverName}>{d.driver_name}</div>
                    {reassigning === d.id ? (
                      <select
                        autoFocus
                        style={styles.truckSelect}
                        defaultValue={truck?.id || ""}
                        onChange={(e) => reassignTruck(d.id, e.target.value || null)}
                        onBlur={() => setReassigning(null)}
                      >
                        <option value="">-- unassign --</option>
                        {trucks.map((t) => (
                          <option key={t.id} value={t.id}>Truck {t.unit_number}</option>
                        ))}
                      </select>
                    ) : (
                      <div style={styles.truckLabel} onClick={() => setReassigning(d.id)} title="Click to reassign truck">
                        {truck ? `Truck ${truck.unit_number}` : "No truck"} <span style={styles.reassignHint}>(reassign)</span>
                      </div>
                    )}
                  </div>

                  {days.map((day, dayIdx) => (
                    <div
                      key={day.toISOString()}
                      style={{ ...styles.dayCell, gridColumn: dayIdx + 2, gridRow: rowNum }}
                      onClick={() => setAddingFor({ driverId: d.id, date: day })}
                    >
                      <span style={styles.emptyCellHint}>+ time off</span>
                    </div>
                  ))}

                  {events.map((e) => (
                    <div
                      key={`${e.kind}-${e.id}`}
                      style={{
                        ...styles.eventBar,
                        ...(e.kind === "timeoff" ? styles.timeOffBar : styles.loadBar),
                        gridColumn: `${e.startCol + 2} / ${e.endCol + 3}`,
                        gridRow: rowNum,
                        marginTop: e.lane * LANE_HEIGHT + 4,
                        marginLeft: `calc(2px + ${e.startInsetPct}%)`,
                        marginRight: `calc(2px + ${e.endInsetPct}%)`,
                        borderTopLeftRadius: e.continuesBefore ? 0 : 6,
                        borderBottomLeftRadius: e.continuesBefore ? 0 : 6,
                        borderTopRightRadius: e.continuesAfter ? 0 : 6,
                        borderBottomRightRadius: e.continuesAfter ? 0 : 6,
                      }}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (e.kind === "load") setLoadDetail(e.raw);
                        else setEditingTimeOff(e.raw);
                      }}
                      title={e.kind === "timeoff" ? `Time off${e.note ? `: ${e.note}` : ""} (click to edit)` : loadTooltip(e.raw)}
                    >
                      {e.continuesBefore && <span style={styles.continuesArrow}>◄</span>}
                      {e.kind === "timeoff" ? (
                        <>
                          <span style={styles.eventBarLabel}>
                            Time off{e.note ? ` -- ${e.note}` : ""}
                          </span>
                          <button
                            style={styles.chipRemove}
                            onClick={(ev) => { ev.stopPropagation(); deleteTimeOff(e.id); }}
                            title="Remove time off"
                          >
                            <X size={10} />
                          </button>
                        </>
                      ) : (
                        <span style={styles.eventBarLabel}>
                          {e.raw.origin_location} → {e.raw.destination_location}
                          <span style={styles.eventBarSub}> · pickup {formatTime(e.pickup)} · delivery {formatTime(e.delivery)}</span>
                        </span>
                      )}
                      {e.continuesAfter && <span style={styles.continuesArrow}>►</span>}
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {addingFor && (
        <TimeOffModal
          title={`Add time off -- ${drivers.find((d) => d.id === addingFor.driverId)?.driver_name || ""}`}
          driverId={addingFor.driverId}
          anchorMode="calendarDay"
          initialStart={addingFor.date}
          initialEnd={addingFor.date}
          defaultEndTime="23:59"
          saving={savingTimeOff}
          onCancel={() => setAddingFor(null)}
          onSave={(start, end, tz, note) => saveTimeOff(addingFor.driverId, start, end, tz, note)}
        />
      )}

      {editingTimeOff && (
        <TimeOffModal
          title={`Edit time off -- ${drivers.find((d) => (d.id === (editingTimeOff.driver_id?.id || editingTimeOff.driver_id)))?.driver_name || ""}`}
          driverId={editingTimeOff.driver_id?.id || editingTimeOff.driver_id}
          anchorMode="instant"
          initialStart={parseUtcNaive(editingTimeOff.start_datetime)}
          initialEnd={parseUtcNaive(editingTimeOff.end_datetime)}
          initialNote={editingTimeOff.note || ""}
          saveLabel="Save changes"
          saving={savingTimeOff}
          onCancel={() => setEditingTimeOff(null)}
          onSave={(start, end, tz, note) => updateTimeOff(editingTimeOff.id, start, end, tz, note)}
        />
      )}

      {loadDetail && <LoadDetailModal load={loadDetail} onClose={() => setLoadDetail(null)} />}
    </div>
  );
}

// US zones covering where this fleet actually operates -- a fixed list
// rather than every IANA zone, since a flat dropdown of ~400 zone names
// would bury the ones a dispatcher would ever pick.
const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
];

// Wall-clock date/time reading of `date` AS SEEN IN `tzName` -- browser-
// native (Intl), works for any IANA zone regardless of the viewer's own
// timezone. Only good for DISPLAY (instant -> local parts); the reverse
// (local parts -> instant) needs the backend's real zoneinfo conversion,
// which is what /fleet/save-time-off does.
function zonedParts(date, tzName) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tzName, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = {};
  fmt.formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}` };
}

function TimeOffModal({
  title, driverId, anchorMode, initialStart, initialEnd, initialNote, defaultEndTime,
  saving, onCancel, onSave, saveLabel = "Add time off",
}) {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone, setTimezone] = useState(
    TIMEZONE_OPTIONS.some((o) => o.value === browserTz) ? browserTz : "America/Chicago"
  );
  const [tzResolved, setTzResolved] = useState(false);
  const [startDate, setStartDate] = useState(toDateInput(initialStart));
  const [startTime, setStartTime] = useState(toTimeInput(initialStart));
  const [endDate, setEndDate] = useState(toDateInput(initialEnd));
  const [endTime, setEndTime] = useState(defaultEndTime || toTimeInput(initialEnd));
  const [note, setNote] = useState(initialNote || "");

  // Default the timezone to the driver's truck's current position -- a
  // starting point, not a lock; the dropdown stays fully editable.
  useEffect(() => {
    if (!driverId) return;
    axios
      .post(`${apiBaseUrl}/scripted/fleet/timezone-default`, { driver_id: driverId }, authHeaders())
      .then((res) => {
        const tz = res.data?.timezone;
        if (tz && TIMEZONE_OPTIONS.some((o) => o.value === tz)) setTimezone(tz);
      })
      .catch(() => {})
      .finally(() => setTzResolved(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  // Editing an existing (real UTC instant) time off -- once the timezone is
  // known, re-derive what the date/time fields should show so they read as
  // that instant's wall clock in the SELECTED zone, not the browser's own.
  // A calendarDay anchor (adding fresh) is just a clicked date label, not a
  // real instant yet, so it doesn't need this -- it stays as typed.
  useEffect(() => {
    if (anchorMode !== "instant" || !tzResolved) return;
    const s = zonedParts(initialStart, timezone);
    const e = zonedParts(initialEnd, timezone);
    setStartDate(s.date); setStartTime(s.time);
    setEndDate(e.date); setEndTime(e.time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, tzResolved, anchorMode]);

  function submit() {
    // Raw local wall-clock strings + the chosen zone -- the backend (same
    // zoneinfo conversion the broker-call wizard already relies on for
    // pickup/delivery) does the actual local->UTC math, so this never
    // silently uses the dispatcher's own browser timezone instead of the
    // zone they explicitly picked.
    onSave(`${startDate}T${startTime}:00`, `${endDate}T${endTime}:00`, timezone, note);
  }

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>Zone</label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={styles.modalDateInput}>
            {TIMEZONE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={styles.modalDateInput} />
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} style={styles.modalTimeInput} />
        </div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>Until</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={styles.modalDateInput} />
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} style={styles.modalTimeInput} />
        </div>
        <div style={styles.modalRow}>
          <label style={styles.modalLabel}>Note</label>
          <input
            type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Optional -- e.g. personal day"
            style={styles.modalNoteInput}
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
          <button style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.saveBtn} onClick={submit} disabled={saving}>{saving ? "Saving…" : saveLabel}</button>
        </div>
      </div>
    </div>
  );
}

function LoadDetailModal({ load, onClose }) {
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>{load.origin_location} → {load.destination_location}</div>
        <DetailRow label="Customer" value={load.customer_name || "—"} />
        <DetailRow label="Status" value={load.status || "—"} />
        <DetailRow label="Pickup" value={load.pickup_datetime ? parseUtcNaive(load.pickup_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"} />
        <DetailRow label="Delivery" value={load.delivery_datetime ? parseUtcNaive(load.delivery_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—"} />
        <DetailRow label="Rate" value={load.rate_offered != null ? `$${Number(load.rate_offered).toLocaleString()}` : "—"} />
        <DetailRow label="Miles" value={load.miles_loaded != null ? `${Number(load.miles_loaded).toLocaleString()} mi` : "—"} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button style={styles.cancelBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.modalRow}>
      <label style={{ ...styles.modalLabel, width: 70 }}>{label}</label>
      <span style={{ fontSize: 12.5, color: "var(--ink)", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function loadTooltip(l) {
  const pickup = l.pickup_datetime ? parseUtcNaive(l.pickup_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  const delivery = l.delivery_datetime ? parseUtcNaive(l.delivery_datetime)?.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  const rate = l.rate_offered != null ? `$${Number(l.rate_offered).toLocaleString()}` : "—";
  const miles = l.miles_loaded != null ? `${Number(l.miles_loaded).toLocaleString()} mi` : "—";
  return `${l.customer_name || "Load"}\n${l.origin_location} -> ${l.destination_location}\nPickup: ${pickup}\nDelivery: ${delivery}\nRate: ${rate} · ${miles}`;
}

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function startOfWeek(d) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // week starts Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function formatShort(d) {
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}
function formatTime(d) {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
function toDateInput(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toTimeInput(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const styles = {
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4 },
  weekNav: { display: "flex", alignItems: "center", gap: 8 },
  navBtn: {
    width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 7, cursor: "pointer",
  },
  weekLabel: { fontSize: 13, fontWeight: 700, color: "var(--ink)", minWidth: 150, textAlign: "center" },
  todayBtn: {
    fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)",
    border: "none", borderRadius: 7, padding: "6px 12px", cursor: "pointer", marginLeft: 6,
  },

  gridWrap: { overflowX: "auto", background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-xs)", padding: 4 },
  loadingMsg: { padding: 20, fontSize: 13, color: "var(--muted)" },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "180px repeat(7, minmax(150px, 1fr))",
    minWidth: 1200,
    columnGap: 1,
    rowGap: 1,
    background: "var(--line)",
  },
  th: { textAlign: "left", padding: "10px 8px", color: "var(--muted)", fontWeight: 600, background: "var(--card)", whiteSpace: "nowrap" },
  dayNum: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  driverCol: { position: "sticky", left: 0, zIndex: 3 },
  driverCell: {
    padding: 8, background: "var(--card)", position: "sticky", left: 0, zIndex: 2,
    borderRight: "1px solid var(--line)", display: "flex", flexDirection: "column", justifyContent: "center",
  },
  driverName: { fontWeight: 700, color: "var(--ink)", fontSize: 12.5 },
  truckLabel: { fontSize: 11, color: "var(--muted)", cursor: "pointer", marginTop: 2 },
  reassignHint: { opacity: 0.6 },
  truckSelect: { fontSize: 11.5, padding: "3px 4px", borderRadius: 5, border: "1px solid var(--line)", marginTop: 3 },

  dayCell: { background: "var(--card)", cursor: "pointer", position: "relative" },
  emptyCellHint: {
    position: "absolute", top: 4, left: 6, fontSize: 10, color: "var(--muted)", opacity: 0,
    transition: "opacity .1s",
  },

  eventBar: {
    position: "relative", zIndex: 1, height: LANE_HEIGHT - 6, marginLeft: 2, marginRight: 2,
    display: "flex", alignItems: "center", gap: 4, padding: "0 6px", cursor: "pointer",
    overflow: "hidden", whiteSpace: "nowrap",
  },
  timeOffBar: {
    background: "rgba(242,163,61,0.85)", color: "#3a2200", fontSize: 10.5, fontWeight: 700,
  },
  loadBar: {
    background: "rgba(37,99,235,0.9)", color: "#fff", fontSize: 10.5, fontWeight: 600,
  },
  eventBarLabel: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 },
  eventBarSub: { opacity: 0.85, fontWeight: 500 },
  continuesArrow: { fontSize: 9, flexShrink: 0, opacity: 0.85 },
  chipRemove: { background: "none", border: "none", cursor: "pointer", color: "#3a2200", padding: 0, display: "flex", flexShrink: 0 },

  modalOverlay: {
    position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
  },
  modalCard: {
    width: 380, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14,
    padding: 18, boxShadow: "0 20px 60px rgba(0,0,0,.4)",
  },
  modalTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 14 },
  modalRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  modalLabel: { fontSize: 12, color: "var(--muted)", width: 42, flexShrink: 0 },
  modalTimeInput: { fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--line)", flex: 1 },
  modalDateInput: { fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--line)", flex: 1 },
  modalNoteInput: { fontSize: 12.5, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--line)", flex: 1 },
  cancelBtn: { fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer" },
  saveBtn: { fontSize: 12, fontWeight: 700, color: "#fff", background: "var(--brand)", border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer" },
};
